"use client"

import Image from "next/image"
import { CheckCircle2 } from "lucide-react"
import { RevealOnScroll } from "@/components/reveal-on-scroll"

type Highlight = {
  title: string
  description: string
}

type GeneralInfoTemplateProps = {
  eyebrow: string
  title: string
  subtitle: string
  description: string
  image: string
  bullets: string[]
  highlights?: Highlight[]
  note?: string
}

export function GeneralInfoTemplate({
  eyebrow,
  title,
  subtitle,
  description,
  image,
  bullets,
  highlights,
  note,
}: GeneralInfoTemplateProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#fff5f5] to-white py-16 sm:py-20 lg:py-24">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle,rgba(248,113,113,0.12)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <RevealOnScroll animationClass="animate-fade-in">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <span className="inline-flex items-center justify-center rounded-full border border-red-200 bg-white px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-red-600 shadow-sm">
              {eyebrow}
            </span>
            <h1 className="mt-6 text-3xl font-serif font-bold leading-tight text-[#1a2142] sm:text-4xl md:text-5xl">
              {title}
            </h1>
            <p className="mt-4 text-base font-semibold text-red-600 sm:text-lg">{subtitle}</p>
            <p className="mt-4 text-base text-[#394567]/85 sm:text-lg">{description}</p>
          </div>
        </RevealOnScroll>

        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <RevealOnScroll animationClass="animate-slide-in-left">
            <div className="relative overflow-hidden rounded-[36px] border border-red-100 bg-gradient-to-br from-red-50 via-white to-white shadow-[0px_24px_55px_rgba(153,27,27,0.12)]">
              <div className="relative aspect-[6/4]">
                <Image
                  src={image}
                  alt={title}
                  fill
                  className="object-cover"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/15 via-transparent to-transparent" />
            </div>
          </RevealOnScroll>

          <RevealOnScroll animationClass="animate-slide-in-right">
            <div>
              <h2 className="text-xl font-serif font-semibold text-[#11193a] sm:text-2xl">
                Key Takeaways
              </h2>
              <ul className="mt-6 space-y-4">
                {bullets.map((bullet, index) => (
                  <li key={index} className="flex items-start gap-3 text-base text-[#2c3558]/90">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              {note ? (
                <div className="mt-8 rounded-3xl border border-red-100 bg-white/70 p-6 shadow-[0_18px_38px_rgba(153,27,27,0.12)]">
                  <p className="text-sm font-medium uppercase tracking-[0.3em] text-red-500">Please Note</p>
                  <p className="mt-2 text-base text-[#2c3558]/90">{note}</p>
                </div>
              ) : null}
            </div>
          </RevealOnScroll>
        </div>

        {highlights && highlights.length > 0 ? (
          <RevealOnScroll animationClass="animate-slide-in-up">
            <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {highlights.map((item, index) => (
                <div
                  key={`${item.title}-${index}`}
                  className="rounded-3xl border border-red-100 bg-white/80 p-6 shadow-[0px_16px_40px_rgba(153,27,27,0.1)]"
                >
                  <h3 className="text-lg font-semibold text-[#11193a]">{item.title}</h3>
                  <p className="mt-3 text-sm text-[#394567]/85">{item.description}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        ) : null}
      </div>
    </section>
  )
}



